/**
 * Cancellation Service for Hardware Order Cancellation feature.
 * Uses the ITO client to cancel hardware orders:
 *
 * Step 1: Store cancellationReason in amznham order details AND set order status to CANCELLED
 * (BEFORE cancelling items — the OrderProcessor needs the reason present when
 * processing downstream events triggered by item cancellations, per CR-263309969)
 * Step 2: Cancel each non-terminal item via updateItemDetails with status CANCELLED
 *
 * Known limitation: If item cancellation fails mid-way, the order-level status is
 * already CANCELLED but some items may remain in their original state. This is by
 * design — the order status + reason must be set first for downstream processing.
 * The error message reflects this partial state to the user.
 *
 * @module OrderCancellation/cancellation-service
 */

import { Order as ITOOrder } from "@amzn/ito-client";
import { ITOConsumers } from "common/types";
import { OrderStatus } from "common/types/orders-types";
import { ITOOrdersService } from "lib/services";

import { CancellationReason, CancellationResult } from "./types";

/** Item statuses considered terminal — these items should not be cancelled again */
const TERMINAL_ITEM_STATUSES = [OrderStatus.CANCELLED, OrderStatus.COMPLETED, OrderStatus.DELIVERED];

/**
 * Determines whether an item status is terminal (already in a final state).
 */
const isTerminalStatus = (status?: string): boolean => {
  if (!status) return false;
  return TERMINAL_ITEM_STATUSES.includes(status.toUpperCase() as OrderStatus);
};

/**
 * Cancels a hardware order using the ITO client.
 *
 * @param orderId - The taxonomy ID of the order to cancel
 * @param reason - The cancellation reason selected by the user
 * @returns CancellationResult indicating success or failure
 */
export const cancelOrder = async (orderId: string, reason: CancellationReason): Promise<CancellationResult> => {
  const ito = new ITOOrdersService(ITOConsumers.AMZNHAM);

  try {
    const csrfToken = await ito.GetSecurityToken();

    const order: ITOOrder = await ito.GetOrder(orderId);

    const currentDetails = (order.details as Record<string, any>) ?? {};

    await ito.UpdateStatus(orderId, OrderStatus.CANCELLED, csrfToken, {
      ...currentDetails,
      cancellationReason: reason,
    });

    const items = order.items ?? [];
    for (const item of items) {
      if (isTerminalStatus(item.status)) {
        continue;
      }

      try {
        await ito.UpdateItemDetails(orderId, item.itemId!, OrderStatus.CANCELLED, csrfToken);
      } catch (itemError) {
        // Order status is already CANCELLED but some items may not be.
        // This partial state is expected — downstream OrderProcessor will
        // handle reconciliation based on the order-level CANCELLED status.
        return {
          success: false,
          error:
            "Your order was cancelled but some items could not be updated. Please contact support if the issue persists.",
        };
      }
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: "We couldn't cancel your order. Please try again or contact support.",
    };
  }
};
